"use client";

import React from "react";
import { MapPin, Phone, Mail, Instagram, ArrowRight, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const CONTACT_INFO = {
  address: "Av. del Libertador 1200, CABA, Argentina",
  phone: "+54 11 4567 8900",
  email: "info@rudichgroup.com.ar",
  instagram: "@rudichgroup"
};
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="89887" className="flex flex-col items-center justify-center py-12 text-center space-y-6 p-8 rounded-none">
        <div className="size-16 rounded-none bg-primary/10 flex items-center justify-center text-primary mb-4">
          <Mail className="size-8" />
        </div>
        {}
        <h3 className="text-2xl font-sans font-bold uppercase tracking-[-0.5px] mb-4">Mensaje Enviado</h3>
        <p className="text-white font-serif mb-4">{successMessage}</p>
        <Button onClick={resetForm} className="mt-8 rounded-none border-0 uppercase tracking-[-0.5px] font-sans font-bold bg-[#e6cd93] text-white hover:bg-[#e6cd93] hover:text-white transition-colors">
          Enviar otro mensaje
        </Button>
      </div>;
  }
  {}
  const inputClasses = "border-0 border-b border-border rounded-none px-0 py-3 h-auto shadow-none focus-visible:ring-0 focus-visible:border-primary bg-transparent text-white placeholder:text-white transition-colors font-serif";
  const labelClasses = "uppercase tracking-[-0.5px] text-xs text-white mb-1 font-sans font-bold";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="nombre" rules={{
        required: "El nombre es requerido"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Nombre Completo *</FormLabel>
              <FormControl>
                <Input placeholder="Juan Pérez" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="text-xs uppercase tracking-[-0.5px] font-sans font-bold" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El correo es requerido",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Correo electrónico inválido"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Correo Electrónico *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="juan@example.com" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="text-xs uppercase tracking-[-0.5px] font-sans font-bold" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="telefono" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Formato de teléfono inválido"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Teléfono</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+54 11 1234 5678" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="text-xs uppercase tracking-[-0.5px] font-sans font-bold" />
            </FormItem>} />

        <FormField control={control} name="asunto" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Asunto</FormLabel>
              <FormControl>
                <Input placeholder="Ejemplo: Consulta de inversión" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="text-xs uppercase tracking-[-0.5px] font-sans font-bold" />
            </FormItem>} />
      </div>

      <FormField control={control} name="mensaje" rules={{
      required: "El mensaje es requerido",
      minLength: {
        value: 10,
        message: "Mínimo 10 caracteres"
      },
      maxLength: {
        value: 1000,
        message: "Máximo 1000 caracteres"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className={labelClasses}>Mensaje *</FormLabel>
            <FormControl>
              <Textarea placeholder="Cuéntanos cómo podemos ayudarte..." className={`resize-none min-h-[120px] ${inputClasses}`} {...field} />
            </FormControl>
            <FormMessage className="text-xs uppercase tracking-[-0.5px] font-sans font-bold" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 text-destructive text-sm uppercase tracking-[-0.5px] font-sans font-bold mb-4">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-16 rounded-none border-0 uppercase tracking-[-0.5px] font-sans font-bold text-lg group bg-[#e6cd93] text-white hover:bg-[#e6cd93] hover:text-white transition-colors">
        {isSubmitting ? <span className="flex items-center gap-2">
            <Loader className="size-5 animate-spin" />
            Enviando...
          </span> : <span className="flex items-center gap-3">
            Enviar
            <ArrowRight className="size-5 group-hover:translate-x-1 transition-transform" />
          </span>}
      </Button>
    </Form>;
}
export default function ConectateConNosotros() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: infoRef,
    isVisible: isInfoVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="canales-de-contacto" className="bg-background text-foreground py-24 md:py-32 font-serif overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl">
        
        <div ref={headerRef} className={`mb-16 md:mb-24 transition-all duration-1000 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl font-bold uppercase tracking-[-0.5px] mb-4 !text-[hsl(42,62%,74%)] !shadow-none !font-serif md:text-6xl">CONTACTO</h2>
          <div className="w-24 h-1 bg-[hsl(45,93%,47%)]"></div>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 mt-6">
          
          {}
          <div ref={formRef} className={`lg:col-span-7 transition-all duration-1000 delay-100 ${isFormVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <FormProvider formKey="contact_form_home" formId="contact_form_home" sectionName="ConectateConNosotros" defaultValues={{
            nombre: "",
            email: "",
            telefono: "",
            asunto: "",
            mensaje: ""
          }} validationRules={{
            nombre: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            telefono: {
              pattern: "phone"
            },
            asunto: {},
            mensaje: {
              required: true,
              minLength: 10,
              maxLength: 1000
            }
          }} submitText="Enviar" successMessage="Gracias por contactarnos. Un asesor de Rudich Group se comunicará con usted a la brevedad." errorMessage="Hubo un error al enviar tu mensaje. Por favor, intenta nuevamente.">
              <ContactFormInner />
            </FormProvider>
          </div>

          {}
          <div ref={infoRef} className={`lg:col-span-5 flex flex-col justify-between transition-all duration-1000 delay-300 ${isInfoVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="space-y-12">
              <div>
                <h3 className="text-sm uppercase tracking-[-0.5px] mb-4 font-sans font-bold !text-[hsl(0,0%,100%)]">Información de Contacto</h3>
                <ul className="space-y-8 !text-[hsl(0,0%,100%)]">
                  
                  <li className="flex items-start gap-4 group">
                    <div className="mt-1 text-[hsl(45,93%,47%)] group-hover:text-[hsl(45,93%,47%)]/80 transition-colors">
                      <MapPin className="size-5" />
                    </div>
                    <div>
                      
                      <p className="text-muted-foreground leading-relaxed font-serif mb-4">Av. Larrazabal 3539, Caba.</p>
                    </div>
                  </li>

                  <li className="flex items-start gap-4 group">
                    <div className="mt-1 text-[hsl(45,93%,47%)] group-hover:text-[hsl(45,93%,47%)]/80 transition-colors">
                      <Mail className="size-5" />
                    </div>
                    <div>
                      <p className="uppercase tracking-[-0.5px] text-sm font-sans font-bold mb-4">Email</p>
                      <Link className="transition-colors font-serif !text-[hsl(0,0%,100%)] hover:text-primary" to="mailto:info@rudichgroup.com.ar">contacto@rudichgroup.com.ar</Link>
                    </div>
                  </li>

                  <li className="flex items-start gap-4 group">
                    <div className="mt-1 text-[hsl(45,93%,47%)] group-hover:text-[hsl(45,93%,47%)]/80 transition-colors">
                      <Phone className="size-5" />
                    </div>
                    <div>
                      <p className="uppercase tracking-[-0.5px] text-sm font-sans font-bold mb-4 !text-[hsl(0,0%,100%)]">Teléfono</p>
                      <Link to={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} className="transition-colors font-serif !text-[hsl(0,0%,100%)] hover:text-primary">+54 11 3384 1271</Link>
                    </div>
                  </li>

                  

                </ul>
              </div>
            </div>

            <div className="mt-16 pt-16">
              <p className="text-xs uppercase tracking-[-0.5px] font-sans font-bold mb-4 !text-[hsl(0,0%,100%)]">
                Atención al cliente disponible de Lunes a Viernes.
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>;
}